/**
 * Type-safe field value types for form fields.
 * These provide compile-time type safety without relying on dynamic schema inference.
 */

import { ControllerRenderProps, FieldValues } from 'react-hook-form';

/**
 * Base form field types that are commonly used across report forms
 */
export interface BaseReportFormValues {
  report_name?: string;
  report_template_name?: string;
  start_datetime: Date;
  end_datetime: Date;
  email?: string;
  countryCode?: string;
  phone?: string;
}

/**
 * Type-safe field value extractors that narrow unknown to specific types
 * These use type guards instead of assertions
 */
export const FormFieldTypes = {
  /**
   * Safely extract string value from a field
   */
  asString: (field: ControllerRenderProps<FieldValues, any>): string => {
    const value = field.value;
    if (value === null || value === undefined) return '';
    return String(value);
  },

  /**
   * Safely extract Date value from a field
   */
  asDate: (
    field: ControllerRenderProps<FieldValues, any>
  ): Date | undefined => {
    const value = field.value;
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? undefined : date;
    }
    return undefined;
  },

  /**
   * Safely extract string array value from a field
   */
  asStringArray: (field: ControllerRenderProps<FieldValues, any>): string[] => {
    const value = field.value;
    if (Array.isArray(value)) {
      return value.filter((v): v is string => typeof v === 'string');
    }
    return [];
  },

  /**
   * Safely extract boolean value from a field
   */
  asBoolean: (field: ControllerRenderProps<FieldValues, any>): boolean => {
    return Boolean(field.value);
  },

  /**
   * Safely extract SelectType array value from a field
   */
  asSelectArray: <T extends { value: string; label: string }>(
    field: ControllerRenderProps<FieldValues, any>
  ): T[] => {
    const value = field.value;
    if (Array.isArray(value)) {
      return value.filter(
        (v): v is T =>
          typeof v === 'object' &&
          v !== null &&
          'value' in v &&
          'label' in v &&
          typeof v.value === 'string' &&
          typeof v.label === 'string'
      );
    }
    return [];
  },

  /**
   * Safely extract object value from a field
   */
  asObject: <T extends Record<string, any>>(
    field: ControllerRenderProps<FieldValues, any>
  ): T | undefined => {
    const value = field.value;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return value as T;
    }
    return undefined;
  },
};

/**
 * Type guard to check if a value is a valid Date
 */
export function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !isNaN(value.getTime());
}

/**
 * Type guard to check if a value is a string
 */
export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Type guard to check if a value is a string array
 */
export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

/**
 * Type guard to check if a value is a SelectType object
 */
export function isSelectType(
  value: unknown
): value is { value: string; label: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in value &&
    'label' in value &&
    typeof (value as any).value === 'string' &&
    typeof (value as any).label === 'string'
  );
}
